import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as authService from "../services/authService";
import type { User } from "../services/authService";
import { formatIstanbulDateTime } from "../utils/dateUtils";
import { safeSetItem, StoragePriority } from "../utils/storage";
import {
  clearAuthNetworkState,
  markAuthSessionEstablished,
  setLogoutInProgress,
} from "../utils/apiClient";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (_email: string, _password: string) => Promise<void>;
  loginWithGoogle: (_params: {
    credential: string;
  }) => Promise<authService.AuthResponse | void>;
  register: (_params: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    phone?: string;
    termsAccepted: boolean;
    usageMode?: "BUSINESS" | "INDIVIDUAL";
    industry?: string;
    size?: string;
    address?: string;
    domain?: string;
  }) => Promise<authService.AuthResponse | void>;
  registerWithGoogle: (_params: {
    credential: string;
    termsAccepted: boolean;
    usageMode?: "BUSINESS" | "INDIVIDUAL";
    organizationName?: string;
  }) => Promise<authService.AuthResponse | void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (_user: User, _token: string) => void;
  setAuthSession: (_response: authService.AuthResponse) => void;
  updateUser: (_updates: Partial<User>) => void;
  clearError: () => void;
  forceLocalLogout: () => void;
}

let checkAuthInFlight: Promise<void> | null = null;
const AUTH_SYNC_EVENT_KEY = "auth_sync_event";
const AUTH_CHANNEL_NAME = "auth_session";

function emitAuthSyncEvent(type: "logout" | "login"): void {
  if (typeof window === "undefined") return;
  const at = Date.now();
  const payload = { type, at };
  try {
    localStorage.setItem(AUTH_SYNC_EVENT_KEY, JSON.stringify(payload));
    if (type === "logout") {
      localStorage.setItem("auth_recent_logout_at", String(at));
    } else {
      localStorage.removeItem("auth_recent_logout_at");
    }
  } catch {
    // Ignore storage quota/availability errors
  }
  try {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // Ignore BroadcastChannel availability errors
  }
}

function clearLocalAuthArtifacts(): void {
  localStorage.removeItem("auth_user");
  localStorage.removeItem("auth-storage");
  localStorage.removeItem("sessionStartTimestamp");
  localStorage.removeItem("sessionLoginTime");
  localStorage.removeItem("sessionPageViews");
  localStorage.removeItem("workspace_selected_channel");
  localStorage.removeItem("ikai_pulse_store");
}

function clearDeadAuthSession(): void {
  clearAuthNetworkState();
  clearLocalAuthArtifacts();
  emitAuthSyncEvent("logout");
  setLogoutInProgress(false);
}

function hasSessionBootstrapHint(): boolean {
  if (typeof window === "undefined") return false;

  const hasOAuthBootstrapHint =
    new URLSearchParams(window.location.search).get("auth") === "google";
  const hasStoredUser = Boolean(localStorage.getItem("auth_user"));
  const recentLogoutAt = Number(
    localStorage.getItem("auth_recent_logout_at") ?? 0
  );
  const isRecentLogout =
    Number.isFinite(recentLogoutAt) &&
    recentLogoutAt > 0 &&
    Date.now() - recentLogoutAt < 90_000;

  return hasOAuthBootstrapHint || (hasStoredUser && !isRecentLogout);
}

function clearOAuthBootstrapHint(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.get("auth") !== "google") return;
  url.searchParams.delete("auth");
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(email, password);

          // Clear per-user UI state before new session
          localStorage.removeItem("ikai_pulse_store");

          safeSetItem(
            "auth_user",
            JSON.stringify(response.user),
            StoragePriority.CRITICAL
          );
          // Track session start for activity tracking
          try {
            const now = new Date();
            safeSetItem(
              "sessionStartTimestamp",
              Date.now().toString(),
              StoragePriority.IMPORTANT
            );
            safeSetItem(
              "sessionLoginTime",
              formatIstanbulDateTime(now, "HH:mm") || "--:--",
              StoragePriority.IMPORTANT
            );
            safeSetItem("sessionPageViews", "0", StoragePriority.IMPORTANT); // Reset page view counter
          } catch (e) {
            console.warn("Failed to save non-critical session data", e);
          }

          set({
            user: response.user,
            token: response.token || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          markAuthSessionEstablished();
          emitAuthSyncEvent("login");
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : "Login failed";
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      loginWithGoogle: async (params: { credential: string }) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.loginWithGoogle(params);

          localStorage.removeItem("ikai_pulse_store");
          safeSetItem(
            "auth_user",
            JSON.stringify(response.user),
            StoragePriority.CRITICAL
          );
          set({
            user: response.user,
            token: response.token || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          markAuthSessionEstablished();
          emitAuthSyncEvent("login");
          return response;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : (error as { message?: string })?.message ||
                "Google ile giriş başarısız oldu. Lütfen tekrar deneyin.";
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      register: async (params: {
        email: string;
        password: string;
        firstName: string;
        lastName: string;
        organizationName?: string;
        phone?: string;
        termsAccepted: boolean;
        usageMode?: "BUSINESS" | "INDIVIDUAL";
        industry?: string;
        size?: string;
        address?: string;
        domain?: string;
      }) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.register(params);

          if (response.requiresVerification || !response.token) {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            return response;
          }

          // Clear per-user UI state before new session
          localStorage.removeItem("ikai_pulse_store");

          safeSetItem(
            "auth_user",
            JSON.stringify(response.user),
            StoragePriority.CRITICAL
          );
          set({
            user: response.user,
            token: response.token || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          markAuthSessionEstablished();
          emitAuthSyncEvent("login");

          return response;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : (error as { message?: string })?.message ||
                "Kayıt başarısız. Lütfen tekrar deneyin.";
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      registerWithGoogle: async (params: {
        credential: string;
        termsAccepted: boolean;
        usageMode?: "BUSINESS" | "INDIVIDUAL";
        organizationName?: string;
      }) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.registerWithGoogle(params);

          localStorage.removeItem("ikai_pulse_store");
          safeSetItem(
            "auth_user",
            JSON.stringify(response.user),
            StoragePriority.CRITICAL
          );
          set({
            user: response.user,
            token: response.token || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          markAuthSessionEstablished();
          emitAuthSyncEvent("login");
          return response;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : (error as { message?: string })?.message ||
                "Google ile kayıt başarısız oldu. Lütfen tekrar deneyin.";
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        clearAuthNetworkState();
        setLogoutInProgress(true);
        try {
          await authService.logout();
        } catch {
          // Logout akisi idempotent: ag hatasi olsa bile local cikis tamamlanir.
        } finally {
          clearLocalAuthArtifacts();
          emitAuthSyncEvent("logout");
          setLogoutInProgress(false);

          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      checkAuth: async () => {
        if (checkAuthInFlight) {
          return checkAuthInFlight;
        }

        checkAuthInFlight = (async () => {
          set({ isLoading: true });
          try {
            if (typeof window !== "undefined") {
              if (!hasSessionBootstrapHint()) {
                clearLocalAuthArtifacts();
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                });
                return;
              }

              const refreshed = await authService.silentRefreshSession();
              if (!refreshed) {
                clearDeadAuthSession();
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                });
                return;
              }
            }

            const user = await authService.getCurrentUser();
            safeSetItem(
              "auth_user",
              JSON.stringify(user),
              StoragePriority.CRITICAL
            );
            clearOAuthBootstrapHint();

            set({
              user,
              token: null,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            markAuthSessionEstablished();
            emitAuthSyncEvent("login");
          } catch {
            if (typeof window !== "undefined") {
              clearDeadAuthSession();
            }
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          } finally {
            checkAuthInFlight = null;
          }
        })();

        return checkAuthInFlight;
      },

      setUser: (user: User, token: string) => {
        safeSetItem(
          "auth_user",
          JSON.stringify(user),
          StoragePriority.CRITICAL
        );
        set({
          user,
          token: token || null,
          isAuthenticated: true,
          error: null,
        });
        markAuthSessionEstablished();
      },

      setAuthSession: (response: authService.AuthResponse) => {
        if (!response?.token || !response?.user) {
          return;
        }

        localStorage.removeItem("ikai_pulse_store");
        safeSetItem(
          "auth_user",
          JSON.stringify(response.user),
          StoragePriority.CRITICAL
        );
        set({
          user: response.user,
          token: response.token || null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        markAuthSessionEstablished();
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = _get().user;
        if (!currentUser) return;

        const updatedUser = { ...currentUser, ...updates };

        // Update localStorage
        safeSetItem(
          "auth_user",
          JSON.stringify(updatedUser),
          StoragePriority.CRITICAL
        );

        set({ user: updatedUser });
      },

      clearError: () => {
        set({ error: null });
      },

      forceLocalLogout: () => {
        clearAuthNetworkState();
        setLogoutInProgress(false);
        clearLocalAuthArtifacts();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
      }),
    }
  )
);
